import { ReactNode } from "react";
import { defaultProps } from "helpers/common";
import { InfoOutlined } from "@mui/icons-material";
import AppLinkText from "components/v2/atoms/app-link-text/AppLinkText";
import { Stack, StackProps, SvgIcon, SvgIconProps, Typography, TypographyProps } from "@mui/material";

export type AlertCardVariant = "success" | "error" | "warning" | "disabled";

export const variantStyles = {
    error: {
        color: "#FF006B",
        backgroundColor: "rgba(255, 0, 107, 0.08)",
    },
    success: {
        color: "#079E8D",
        backgroundColor: "rgba(9, 202, 180, 0.08)",
    },
    warning: {
        color: "#FF9472",
        backgroundColor: "rgba(255, 148, 114, 0.08)",
    },
    disabled: {
        color: "#6D83A3",
        backgroundColor: "rgba(109, 131, 163, 0.08)",
    },
};

export interface AlertCardProps {
    heading: ReactNode;
    text: ReactNode;
    variant?: AlertCardVariant;
    stackProps?: StackProps;
    iconProps?: SvgIconProps;
    headingProps?: TypographyProps;
    linkProps?: TypographyProps;
    textProps?: TypographyProps;
}

function AlertCard(props: AlertCardProps) {
    const variant = props.variant || "disabled";

    const stackProps = defaultProps(props?.stackProps || {}, {
        sx: {
            gap: 0.5,
            padding: "6px 16px",
            borderRadius: "6px",
            ...variantStyles[variant],
        },
    });

    const headingStackProps: StackProps = {
        sx: {
            gap: 1.5,
            flexDirection: "row",
            alignItems: "center",
        },
    };

    const iconProps = defaultProps(props?.iconProps || {}, {
        component: InfoOutlined,
        sx: {
            fontSize: {
                xs: 14,
                lg: 16,
            },
        },
    });

    const headingProps: TypographyProps = defaultProps(props?.headingProps || {}, {
        sx: {
            flex: 1,
            fontSize: {
                xs: 12,
                lg: 14,
            },
            fontWeight: 600,
            lineHeight: 1.5,
            color: "inherit",
        },
        children: props.heading,
    });

    const linkProps: TypographyProps = defaultProps(props?.linkProps || {}, {
        sx: {
            fontSize: {
                xs: 12,
                lg: 13,
            },
            fontWeight: 500,
            lineHeight: 1.5,
            color: "inherit",
        },
    });

    const textProps: TypographyProps = defaultProps(props?.textProps || {}, {
        sx: {
            fontSize: {
                xs: 11,
                lg: 12,
            },
            lineHeight: 1.5,
            color: "inherit",
        },
        children: props.text,
    });

    return (
        <Stack {...stackProps}>
            <Stack {...headingStackProps}>
                <SvgIcon {...iconProps} />
                <Typography {...headingProps} />
                {props.linkProps && <AppLinkText {...linkProps} />}
            </Stack>
            <Typography {...textProps} />
        </Stack>
    );
}

export default AlertCard;
